import dayjs from "dayjs";
import queryString from "query-string";

import { colorsPalettes } from "@similarweb/styles";

import { CATEGORY_PREFIX } from "app/common/services/categoryService";
import { EAvailabilityMode } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    abbrNumberVisitsWithNullFilter,
    abbrNumberWithNASupportVisitsFilter,
    minVisitsAbbrFilter,
} from "app/filters/ngFilters";
import { PdfExportService } from "app/services/PdfExportService";
import { allTrackers } from "app/services/track/track";

export const WEB_SOURCE_DISPLAY_NAME = {
    MobileWeb: "Mobile web",
    Total: "All traffic",
    Desktop: "Desktop",
};

export const POP_COMPARE_EXCEL_END_POINT = "widgetApi/AssetsCompare/Overview/ExcelPOP";
export const POP_SINGLE_CHART_TYPE = "ComparedLine";
// the following endpoints support receiving a category param and
// return data which includes data for the received category as well
export const ENDPOINT_PREFIX_WITH_CATEGORY_SUPPORT = "widgetApi/AssetsCompare";
const overviewMetricsEndpoint = `${ENDPOINT_PREFIX_WITH_CATEGORY_SUPPORT}/Overview`;
export const OVERVIEW_TABLE_ENDPOINT = `${overviewMetricsEndpoint}/LeadersTable`;
export const OVERVIEW_AVERAGES_ENDPOINT = `${overviewMetricsEndpoint}/Table`;
export const OVERVIEW_EXCEL_ENDPOINT = `${overviewMetricsEndpoint}/Excel`;
export const UNIQUE_USERS_EXCEL_ENDPOINT = `${ENDPOINT_PREFIX_WITH_CATEGORY_SUPPORT}/UniqueUsers/Excel`;

// Disable Month granularity in 28 days mode
export const isTimeGranularityDisabled = (props, meta) => {
    const { is28Days, isFromAlternativeSources } = meta;
    const { title } = props;

    // case that alternative sources is true then we want to disable daily/weekly granularity:
    if (isFromAlternativeSources && title !== "M" && !is28Days) {
        return true;
    }
    return is28Days && props.title === "M";
};

// Disable the Month granularity
export const isTimeGranularityDisabledOnlyMonth = ({ title }) => title === "D" || title === "W";

// define the most common use cases of graph type, in order to choose another type the GenericGraph instance
// should implement this method and compose it.
export const getChartType = (meta) => {
    const { isSingleMode, isNonTotalWebSource, isCategoryActive } = meta;
    if (isSingleMode && !isNonTotalWebSource && !isCategoryActive) {
        return "area";
    }
    return "line";
};

// define the most common use cases of web source, in order to choose another one the GenericGraph instance
// should implement this method and compose it.
export const getWebSourceForApiPOP = ({ webSource, chartType }) => {
    if (chartType === "column" && webSource === "Total") {
        return "Combined";
    }
    return webSource;
};

export const getGetExcel = (prefix) => (apiParams) => prefix + "?" + queryString.stringify(apiParams);

const getDateFormatter =
    (format) =>
    ({ value }) =>
        dayjs.utc(value).format(format);
export const dateFormatter = getDateFormatter("MMM YY");
export const minVisitsAbbrFormatter = ({ value }) => minVisitsAbbrFilter()(value);
export const visitsNumberAbbrFormatter = ({ value }) => abbrNumberWithNASupportVisitsFilter()(value);
export const YAxisVisitsNumberAbbrFormatter = ({ value }) => abbrNumberVisitsWithNullFilter()(value);

export const getrInitPOPItemsSingleModeWebSources = ({ durations }) => [
    {
        name: durations[1],
        isSelected: true,
        color: colorsPalettes.orange[400],
        transparentColor: colorsPalettes.orange[200],
    },
    {
        name: durations[0],
        isSelected: true,
        color: colorsPalettes.blue[400],
        transparentColor: colorsPalettes.blue[200],
    },
];

export const webSources = [
    { name: "Desktop", isSelected: true, color: colorsPalettes.blue[400], alias: "Desktop" },
    { name: "Mobile Web", isSelected: true, color: colorsPalettes.sky[300], alias: "MobileWeb" },
    { name: "Total", isSelected: true, color: colorsPalettes.blue[400], alias: "Total" },
];

export const getDualCalculations = (isGAVerified = false) => {
    const i18nInstance = i18nFilter();
    return [
        {
            name: i18nInstance("wa.traffic.engagement.calculation.new"),
            isSelected: true,
            color: colorsPalettes.blue[400],
            alias: "newCalculation",
            isBeta: true,
        },
        {
            name: i18nInstance(
                isGAVerified ? "wa.traffic.engagement.calculation.ga" : "wa.traffic.engagement.calculation.current",
            ),
            isSelected: true,
            color: colorsPalettes.sky[300],
            alias: "currentCalculation",
        },
    ];
};

// for the rare case of columns vs area [ and not columns vs lines]
export const chartGranularityItemClickArea = (chartIndex) => {
    return chartIndex === 1 ? "area" : "column";
};

export const neverStacking = () => false;

export const pngDownload = (chartRef, fileName, trackingEvent?): Promise<void> => {
    if (trackingEvent) {
        trackingEvent();
    } else {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Over Time Graph/Engagement Overview/PNG");
    }

    const offSetX = 0;
    const offSetY = 105;
    const styleHTML = PdfExportService.getStylesheetsForPng();
    return PdfExportService.downloadHtmlPngFedService(
        styleHTML + chartRef.outerHTML,
        fileName,
        chartRef.offsetWidth + offSetX,
        chartRef.offsetHeight + offSetY,
    );
};

export const addBetaBranchParam = (params, add = true, noGAVerified = true) =>
    add ? { ...params, beta: true, ...(noGAVerified ? { ShouldGetVerifiedData: false } : {}) } : params;

export const addCategoryParam = (key, categoryParam) => key + "," + CATEGORY_PREFIX + categoryParam;

export const hasOpenAiInsightsPermissions = () =>
    swSettings.components?.TrafficAndEngagement?.resources?.OpenAiInsights === EAvailabilityMode.OPEN;
